import { AnalyticsEventNames } from "../utils/analyticsEvents";
import { PHLEBOTOMY_CUSTOMER_IO_SMS_SOURCE } from "../utils/urlConstants";
import { TrackAnalyticsEvent } from "./Analytics";

export type PhlebotomyFlow = "finder_v2" | "legacy_hub";

export interface PhlebotomyTrackingMetadata {
  flow?: PhlebotomyFlow;
  lims_id?: string;
  clinic_public_id?: string;
  user_id?: string;
  page_name?: string;
  click_type?: string;
  link_value?: string;
  location_name?: string;
  kit_status?: "has_kit" | "needs_kit";
  tracking_id?: string;
  [key: string]: string | number | undefined;
}

export interface PhlebotomyFinderTrackingContext {
  distinctId: string;
  flow: PhlebotomyFlow;
  pageName: string;
  clinicPublicId?: string;
  limsId?: string;
  userId?: string;
  trackingId?: string;
}

const buildTrackingProperties = (
  context: PhlebotomyFinderTrackingContext,
  extra: Record<string, string | number> = {},
): Record<string, string | number> => {
  const properties: Record<string, string | number> = {
    flow: context.flow,
    page_name: context.pageName,
    ...extra,
  };

  if (context.clinicPublicId) {
    properties.clinic_public_id = context.clinicPublicId;
  }
  if (context.limsId) {
    properties.lims_id = context.limsId;
  }
  if (context.userId) {
    properties.user_id = context.userId;
  }
  if (context.trackingId) {
    properties.tracking_id = context.trackingId;
  }

  return properties;
};

export const trackPhlebotomyFinderEvent = (
  eventName: AnalyticsEventNames,
  context: PhlebotomyFinderTrackingContext,
  extraProps: Record<string, string | number> = {},
): Promise<void> =>
  TrackAnalyticsEvent(
    eventName,
    undefined,
    context.distinctId,
    buildTrackingProperties(context, extraProps),
  ).then(() => undefined);

export const buildPhlebotomyFinderTrackingContext = (params: {
  distinctId: string;
  clinicPublicId?: string | null;
  limsId?: string | null;
  userId?: string | null;
  trackingId?: string | null;
  pageName?: string;
}): PhlebotomyFinderTrackingContext => ({
  distinctId: params.distinctId,
  flow: "finder_v2",
  pageName: params.pageName ?? "PhlebotomySearchPage",
  clinicPublicId: params.clinicPublicId ?? undefined,
  limsId: params.limsId ?? undefined,
  userId: params.userId ?? undefined,
  trackingId: params.trackingId ?? undefined,
});

export const trackPhlebotomyAction = async (
  eventName: AnalyticsEventNames,
  limsId: string | undefined,
  userId: string,
  extraProps: Record<string, string | number> = {},
): Promise<void> => {
  const properties: Record<string, string | number> = {
    flow: "legacy_hub",
    user_id: userId,
    ...extraProps,
  };
  if (limsId) {
    properties.lims_id = limsId;
  }
  await TrackAnalyticsEvent(eventName, undefined, userId, properties);
};

export const trackPhlebotomyPageView = async (
  pageName:
    | "PhlebotomyPage"
    | "PhlebotomyLocationFinderPage"
    | "PhlebotomyPhoneOptionsPage",
  limsId: string | undefined,
  userId: string,
): Promise<void> => {
  let eventName: AnalyticsEventNames;
  if (pageName === "PhlebotomyPage") {
    eventName = AnalyticsEventNames.PhlebotomyPageView;
  } else if (pageName === "PhlebotomyLocationFinderPage") {
    eventName = AnalyticsEventNames.PhlebotomyLocationFinderPageView;
  } else if (pageName === "PhlebotomyPhoneOptionsPage") {
    eventName = AnalyticsEventNames.PhlebotomyPhoneOptionsPageView;
  } else {
    console.error(`Invalid page name to trackPhlebotomyPageView: ${pageName}`);
    return;
  }

  await trackPhlebotomyAction(eventName, limsId, userId, {
    page_name: pageName,
  });
};

export const trackPhlebotomyClick = async (
  clickType:
    | "schedule_now"
    | "find_location"
    | "location_selected"
    | "location_phone"
    | "location_email"
    | "location_directions"
    | "location_schedule"
    | "contact_us"
    | "mobile_phleb_phone"
    | "mobile_phleb_link"
    | "back_button",
  limsId: string,
  userId: string,
  additionalMetadata?: PhlebotomyTrackingMetadata,
): Promise<void> => {
  const metadata: Record<string, string | number> = {
    click_type: clickType,
    ...(additionalMetadata || {}),
  };

  await trackPhlebotomyAction(
    AnalyticsEventNames.PhlebotomyClick,
    limsId,
    userId,
    metadata,
  );
};

export const trackPhlebotomyFAQClick = async (
  faqKey: string,
  limsId: string,
  userId: string,
): Promise<void> => {
  const metadata = {
    click_type: `faq_click_${faqKey}`,
    faq_key: faqKey,
  };

  await trackPhlebotomyAction(
    AnalyticsEventNames.PhlebotomyFAQClick,
    limsId,
    userId,
    metadata,
  );
};

export type PhlebotomySearchSource = "zip" | "address" | "geolocation";

export type PhlebotomySearchEntryPath = "intake" | "sms_return" | "direct";

export const trackPhlebotomySearchPageView = (
  context: PhlebotomyFinderTrackingContext,
  entryPath: PhlebotomySearchEntryPath,
): Promise<void> =>
  trackPhlebotomyFinderEvent(
    AnalyticsEventNames.PhlebotomySearchPageView,
    context,
    {
      search_entry_path: entryPath,
    },
  );

export const trackPhlebotomySearchPerformed = (
  context: PhlebotomyFinderTrackingContext,
  source: PhlebotomySearchSource,
  resultCount: number,
  /** True when results exist but none are within the nearby display band. */
  outsideNearbyBand = false,
  entryPath: PhlebotomySearchEntryPath,
): Promise<void> =>
  trackPhlebotomyFinderEvent(
    AnalyticsEventNames.PhlebotomySearchPerformed,
    context,
    {
      search_source: source,
      search_entry_path: entryPath,
      result_count: resultCount,
      outside_nearby_band: outsideNearbyBand ? 1 : 0,
    },
  );

export type PhlebotomySearchClickType =
  | "location_selected"
  | "location_phone"
  | "location_email"
  | "location_directions"
  | "location_schedule"
  | "change_location"
  | "back_to_results"
  | "mobile_phlebotomy_options_opened"
  | "mobile_phlebotomy_schedule_opened"
  | "mobile_phlebotomy_location_submitted"
  | "mobile_phlebotomy_availability_shown"
  | "mobile_phlebotomy_slot_selected"
  | "mobile_phlebotomy_getlabs_book_submitted"
  | "mobile_phlebotomy_getlabs_book_succeeded"
  | "mobile_phlebotomy_getlabs_book_failed"
  | "mobile_phlebotomy_scheduling_needed_selected"
  | "mobile_phlebotomy_already_scheduled_selected"
  | "mobile_phlebotomy_existing_schedule_support_called"
  | "mobile_phlebotomy_care_team_fallback"
  | "mobile_phlebotomy_request_opened"
  | "mobile_phlebotomy_kit_status_selected"
  | "mobile_phlebotomy_request_submitted"
  | "mobile_phlebotomy_website_opened"
  | "mobile_phlebotomy_phone_call"
  | "mobile_phlebotomy_phone_text"
  | "faq_support_email"
  | "faq_support_phone";

export const trackPhlebotomySearchFAQClick = (
  context: PhlebotomyFinderTrackingContext,
  faqKey: string,
): Promise<void> =>
  trackPhlebotomyFinderEvent(AnalyticsEventNames.PhlebotomyFAQClick, context, {
    click_type: `faq_click_${faqKey}`,
    faq_key: faqKey,
  });

export interface PhlebotomyFaqTrackingHandlers {
  onFAQClick?: (faqKey: string) => void;
  onSupportEmailClick?: () => void;
  onSupportPhoneClick?: () => void;
}

export const trackPhlebotomySearchClick = (
  context: PhlebotomyFinderTrackingContext,
  clickType: PhlebotomySearchClickType,
  additionalMetadata: Pick<
    PhlebotomyTrackingMetadata,
    "location_name" | "kit_status"
  > = {},
): Promise<void> =>
  trackPhlebotomyFinderEvent(AnalyticsEventNames.PhlebotomyClick, context, {
    click_type: clickType,
    ...additionalMetadata,
  });

/** Wire inline FAQ interactions to finder_v2 analytics. */
export const buildPhlebotomyFaqTrackingHandlers = (
  context: PhlebotomyFinderTrackingContext | null | undefined,
): PhlebotomyFaqTrackingHandlers => {
  if (!context) {
    return {};
  }

  const track = (promise: Promise<void>) => {
    promise.catch(() => undefined);
  };

  return {
    onFAQClick: (faqKey: string) =>
      track(trackPhlebotomySearchFAQClick(context, faqKey)),
    onSupportEmailClick: () =>
      track(trackPhlebotomySearchClick(context, "faq_support_email")),
    onSupportPhoneClick: () =>
      track(trackPhlebotomySearchClick(context, "faq_support_phone")),
  };
};

export const trackPhlebotomyReminderLinkClicked = (
  context: PhlebotomyFinderTrackingContext,
): Promise<void> => {
  if (!context.trackingId && !context.userId) {
    return Promise.resolve();
  }

  return trackPhlebotomyFinderEvent(
    AnalyticsEventNames.PhlebotomyReminderLinkClicked,
    context,
    {
      campaign_source: PHLEBOTOMY_CUSTOMER_IO_SMS_SOURCE,
    },
  );
};

/**
 * Track when a user views the phlebotomy reminder page
 */
export const trackPhlebotomyReminderPageView = async (
  accountId: string | undefined,
  submittedByType: "patient" | "provider",
  trackingId: string,
): Promise<void> => {
  await TrackAnalyticsEvent(
    AnalyticsEventNames.PhlebotomyReminderPageView,
    undefined,
    trackingId,
    {
      flow: "finder_v2",
      page_name: "PhlebotomyReminderPage",
      submitted_by_type: submittedByType,
      scope: accountId ? "clinic" : "universal",
      ...(accountId ? { portal_account_public_id: accountId } : {}),
    },
  );
};

/**
 * Track when a user successfully submits the phlebotomy reminder form
 */
export const trackPhlebotomyReminderSubmitted = async (
  accountId: string | undefined,
  submittedBy: "patient" | "provider",
  trackingId: string,
): Promise<void> => {
  await TrackAnalyticsEvent(
    AnalyticsEventNames.PhlebotomyReminderSubmitted,
    undefined,
    trackingId,
    {
      flow: "finder_v2",
      page_name: "PhlebotomyReminderPage",
      submitted_by: submittedBy,
      scope: accountId ? "clinic" : "universal",
      ...(accountId ? { portal_account_public_id: accountId } : {}),
    },
  );
};
